import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, FloppyDisk, CheckCircle, Clock, Lightning,
  AirplaneTilt, Anchor, TreeStructure, ListChecks, WarningOctagon,
  CaretDown, CaretUp, Eye, Robot
} from '@phosphor-icons/react';

const FINDING_STATUS_DOT = { completed: 'bg-emerald-500', in_progress: 'bg-amber-500' };
const SYNTHESIS_STATUS_STYLE = {
  completed: 'bg-emerald-100 text-emerald-800',
  in_progress: 'bg-blue-100 text-blue-800',
};
const API = '/api';
const SYNTHESIS_STATUS_LABEL = { completed: 'Complete', in_progress: 'In Progress' };

const STATUS_COLORS = {
  completed: 'bg-emerald-500',
  in_progress: 'bg-amber-500',
  pending: 'bg-slate-300',
  escalated: 'bg-red-500',
  not_started: 'bg-slate-300',
};

function SpecialistFindingsCard({ finding, onToggle, isOpen }) {
  if (!finding?.data || Object.keys(finding.data).length === 0) return null;

  const filledCount = Object.entries(finding.data).filter(([, v]) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.trim().length > 0;
    return v !== null && v !== undefined;
  }).length;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`finding-card-${finding.specialist}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid={`toggle-finding-${finding.specialist}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${FINDING_STATUS_DOT[finding.status] || 'bg-slate-300'}`} />
          <span className="font-semibold text-sm text-slate-800">{finding.specialist}</span>
          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">{filledCount} fields</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${finding.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {finding.status || 'not started'}
          </span>
        </div>
        {isOpen ? <CaretUp size={16} className="text-slate-500" /> : <CaretDown size={16} className="text-slate-500" />}
      </button>
      {isOpen && (
        <div className="px-5 py-4 space-y-3 bg-white">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(finding.data).map(([key, value]) => {
              if (value === null || value === undefined || value === '') return null;
              const displayVal = Array.isArray(value) ? value.join(', ') : String(value);
              if (!displayVal.trim()) return null;
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const isLongText = displayVal.length > 100;
              return (
                <div key={key} className={`${isLongText ? 'col-span-2' : ''}`}>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</dt>
                  <dd className="text-sm text-slate-800 mt-0.5">{displayVal}</dd>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskCompletionGrid({ tasks }) {
  if (!tasks?.length) return <p className="text-sm text-slate-500">No tasks generated yet.</p>;
  const bySpecialist = {};
  tasks.forEach(t => {
    const spec = t.specialist_required || 'General';
    if (!bySpecialist[spec]) bySpecialist[spec] = [];
    bySpecialist[spec].push(t);
  });

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-slate-700">Module Completion</span>
        <span className="text-sm text-slate-500">{completedCount}/{totalCount} tasks ({pct}%)</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        {Object.entries(bySpecialist).map(([spec, specTasks]) => {
          const done = specTasks.every(t => t.status === 'completed');
          const inProg = specTasks.some(t => t.status === 'in_progress');
          const esc = specTasks.some(t => t.status === 'escalated');
          let statusDot = 'bg-slate-300';
          if (done) statusDot = 'bg-emerald-500';
          else if (esc) statusDot = 'bg-red-500';
          else if (inProg) statusDot = 'bg-amber-500';
          return (
            <div key={spec} className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 bg-slate-50">
              <div className={`w-2 h-2 rounded-full ${statusDot}`} />
              <span className="text-xs text-slate-700 truncate">{spec}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Synthesis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'iic';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [sections, setSections] = useState({});
  const [synthesisStatus, setSynthesisStatus] = useState('not_started');
  const [lastSaved, setLastSaved] = useState(null);
  const [openFindings, setOpenFindings] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [aiGenerating, setAiGenerating] = useState(null); // section key currently generating

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investigations/${id}/synthesis`, { withCredentials: true });
      setData(res.data);
      if (res.data.synthesis?.sections) {
        setSections(res.data.synthesis.sections);
        setSynthesisStatus(res.data.synthesis.status || 'not_started');
        setLastSaved(res.data.synthesis.last_saved);
      }
    } catch (e) {
      console.error('Failed to load synthesis data:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (status) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/investigations/${id}/synthesis`, {
        sections,
        status: status || synthesisStatus,
      }, { withCredentials: true });
      setSynthesisStatus(status || synthesisStatus);
      setLastSaved(res.data.last_saved);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving synthesis');
    } finally {
      setSaving(false);
    }
  };

  const handleSectionChange = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const handleAiGenerate = async (cat) => {
    setAiGenerating(cat.key);
    try {
      const res = await axios.post(`${API}/investigations/${id}/synthesis/ai-generate`, {
        section_key: cat.key,
        section_label: cat.label,
        section_description: cat.description,
        existing_content: sections[cat.key] || '',
      }, { withCredentials: true });

      if (res.data.type === 'list') {
        handleSectionChange(cat.key, res.data.content);
      } else {
        handleSectionChange(cat.key, res.data.content);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'AI generation failed. Please try again.';
      alert(msg);
    } finally {
      setAiGenerating(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-slate-500">Loading synthesis...</div></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Unable to load synthesis data</div>;

  const { investigation, tasks_summary, specialist_findings, categories } = data;
  const findingsWithData = specialist_findings.filter(f => f.data && Object.keys(f.data).length > 0);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: ListChecks },
    { key: 'findings', label: 'Specialist Findings', icon: Eye },
    { key: 'synthesis', label: 'Synthesis', icon: TreeStructure },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="synthesis-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate(`/investigations/${id}`)} data-testid="back-to-investigation">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Module 17: Information Synthesis</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${investigation?.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
              {investigation?.sector === 'aviation' ? <AirplaneTilt size={12} className="inline mr-1" /> : <Anchor size={12} className="inline mr-1" />}
              {investigation?.sector}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SYNTHESIS_STATUS_STYLE[synthesisStatus] || 'bg-slate-200 text-slate-700'}`}>
              {SYNTHESIS_STATUS_LABEL[synthesisStatus] || 'Not Started'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
            <span className="font-mono">{investigation?.investigation_number}</span>
            <span>{investigation?.title}</span>
            {lastSaved && <span>Last saved: {new Date(lastSaved).toLocaleString()}</span>}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-6" data-testid="synthesis-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.key === 'findings' && findingsWithData.length > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{findingsWithData.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6" data-testid="overview-tab">
          {/* Investigation Summary Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
              <WarningOctagon size={16} className="text-amber-600" /> Investigation Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-slate-500 block text-xs">Severity</span><span className="font-semibold text-slate-800">{investigation?.severity || '-'}</span></div>
              <div><span className="text-slate-500 block text-xs">Status</span><span className="font-semibold text-slate-800 capitalize">{investigation?.status || '-'}</span></div>
              <div><span className="text-slate-500 block text-xs">Occurrence Date</span><span className="font-semibold text-slate-800">{investigation?.occurrence_date ? new Date(investigation.occurrence_date).toLocaleDateString() : '-'}</span></div>
              <div><span className="text-slate-500 block text-xs">Specialist Findings</span><span className="font-semibold text-slate-800">{findingsWithData.length} submitted</span></div>
            </div>
          </div>

          {/* Task Completion */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
              <ListChecks size={16} className="text-blue-600" /> Task Completion Status
            </h2>
            <TaskCompletionGrid tasks={tasks_summary} />
          </div>

          {/* Readiness Indicators */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Lightning size={16} className="text-amber-500" /> Synthesis Readiness
            </h2>
            {(() => {
              const total = tasks_summary?.length || 0;
              const completed = tasks_summary?.filter(t => t.status === 'completed').length || 0;
              const allDone = total > 0 && completed === total;
              const hasFindings = findingsWithData.length > 0;
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${hasFindings ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {hasFindings ? <CheckCircle size={14} className="text-emerald-600" /> : <Clock size={14} className="text-slate-400" />}
                    </div>
                    <span className="text-sm text-slate-700">Specialist findings submitted ({findingsWithData.length})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${allDone ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {allDone ? <CheckCircle size={14} className="text-emerald-600" /> : <Clock size={14} className="text-slate-400" />}
                    </div>
                    <span className="text-sm text-slate-700">All specialist tasks completed ({completed}/{total})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${synthesisStatus === 'completed' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      {synthesisStatus === 'completed' ? <CheckCircle size={14} className="text-emerald-600" /> : <Clock size={14} className="text-slate-400" />}
                    </div>
                    <span className="text-sm text-slate-700">Synthesis analysis {synthesisStatus === 'completed' ? 'complete' : 'pending'}</span>
                  </div>
                  {!allDone && (
                    <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mt-2">
                      Note: You may begin the synthesis while specialist tasks are still in progress. Final conclusions should be drawn after all modules are complete.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {activeTab === 'findings' && (
        <div className="space-y-3" data-testid="findings-tab">
          {findingsWithData.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-slate-500">No specialist findings have been submitted yet.</p>
              <p className="text-xs text-slate-400 mt-1">Investigators need to complete their specialist modules first.</p>
            </div>
          ) : (
            findingsWithData.map((f) => (
              <SpecialistFindingsCard
                key={f.task_id}
                finding={f}
                isOpen={!!openFindings[f.task_id]}
                onToggle={() => setOpenFindings(prev => ({ ...prev, [f.task_id]: !prev[f.task_id] }))}
              />
            ))
          )}
        </div>
      )}

      {activeTab === 'synthesis' && (
        <div className="space-y-6" data-testid="synthesis-tab">
          {!canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
              You have view-only access. Only the IIC or Admin can edit the synthesis.
            </div>
          )}
          {categories?.map((cat) => (
            <div key={cat.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`section-${cat.key}`}>
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{cat.label}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{cat.description}</p>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-purple-300 text-purple-700 hover:bg-purple-50 text-xs"
                    onClick={() => handleAiGenerate(cat)}
                    disabled={aiGenerating === cat.key}
                    data-testid={`ai-generate-${cat.key}`}
                  >
                    <Robot size={14} className={`mr-1.5 ${aiGenerating === cat.key ? 'animate-spin' : ''}`} />
                    {aiGenerating === cat.key ? 'Generating...' : 'AI Generate'}
                  </Button>
                )}
              </div>
              <div className="p-5">
                {cat.key === 'probable_causes' || cat.key === 'contributing_factors' || cat.key === 'safety_recommendations' ? (
                  <div className="space-y-3">
                    {(sections[cat.key] || ['']).map((item, idx) => (
                      <div key={`${cat.key}-item-${idx}`} className="flex items-start gap-2">
                        <span className="text-xs font-mono text-slate-400 mt-2.5 shrink-0 w-5">{idx + 1}.</span>
                        <Textarea
                          value={item}
                          onChange={(e) => {
                            if (!canEdit) return;
                            const updated = [...(sections[cat.key] || [''])];
                            updated[idx] = e.target.value;
                            handleSectionChange(cat.key, updated);
                          }}
                          readOnly={!canEdit}
                          placeholder={`Enter ${cat.label.toLowerCase()} item...`}
                          className="rounded-lg min-h-[60px] text-sm"
                          data-testid={`input-${cat.key}-${idx}`}
                        />
                        {canEdit && (sections[cat.key] || ['']).length > 1 && (
                          <button
                            onClick={() => {
                              const updated = (sections[cat.key] || ['']).filter((_, i) => i !== idx);
                              handleSectionChange(cat.key, updated);
                            }}
                            className="text-red-400 hover:text-red-600 mt-2 text-xs shrink-0"
                            data-testid={`remove-${cat.key}-${idx}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs"
                        onClick={() => handleSectionChange(cat.key, [...(sections[cat.key] || ['']), ''])}
                        data-testid={`add-${cat.key}`}
                      >
                        + Add Item
                      </Button>
                    )}
                  </div>
                ) : (
                  <Textarea
                    value={sections[cat.key] || ''}
                    onChange={(e) => canEdit && handleSectionChange(cat.key, e.target.value)}
                    readOnly={!canEdit}
                    placeholder={`Enter ${cat.label.toLowerCase()}...`}
                    className="rounded-lg min-h-[120px] text-sm"
                    data-testid={`input-${cat.key}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      {canEdit && (
        <div className="sticky bottom-0 bg-white border-t border-slate-200 mt-6 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 flex items-center justify-between z-10" data-testid="synthesis-action-bar">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock size={14} />
            {lastSaved ? `Last saved: ${new Date(lastSaved).toLocaleString()}` : 'Not saved yet'}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-lg" onClick={() => handleSave('in_progress')} disabled={saving} data-testid="save-synthesis-draft">
              <FloppyDisk size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg" onClick={() => handleSave('completed')} disabled={saving} data-testid="mark-synthesis-complete">
              <CheckCircle size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Mark Complete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
